/**
 * Задачи проекта — BFF /api/bff/projects/:projectId/work/*
 */
import { getApiQueryClient } from '@/lib/api/query-client-registry';
import { getBffRequestHeaders } from '@/lib/api/bff-request-headers';
import { notifyBffUnauthorized } from '@/lib/auth/auth-watchdog';
import { invalidateListChannelsCache } from '@/hooks/useChatApi';
import { throwApiError } from '@/utils/error-to-user-message';

const jsonHeaders = () => ({
  ...getBffRequestHeaders({ 'Content-Type': 'application/json' }),
});

/**
 * Заголовки для мутирующих write-ручек, на которые на BFF навешан
 * `IdempotencyInterceptor`. Idempotency-Key — UUID v4 на каждый вызов (новый
 * для каждой логической операции); если потребуется ретрай — клиентский код
 * должен передавать тот же ключ через параметр `idempotencyKey`.
 */
const idempotentJsonHeaders = (idempotencyKey?: string): Record<string, string> => ({
  ...jsonHeaders(),
  'Idempotency-Key': idempotencyKey ?? crypto.randomUUID(),
});

/** То же, что выше, но без `Content-Type` — для DELETE/без-body запросов. */
const idempotentHeaders = (idempotencyKey?: string): Record<string, string> => ({
  ...getBffRequestHeaders(),
  'Idempotency-Key': idempotencyKey ?? crypto.randomUUID(),
});

export interface WorkflowStatusApi {
  id: string;
  name: string;
  slug: string;
  category: string;
  sort_order: number;
  color: string | null;
}

export interface WorkflowResponse {
  workflow_id: string;
  /** Команда workflow в проекте. NULL — legacy общий workflow для бэклог-задач без команды. */
  team_title: string | null;
  statuses: WorkflowStatusApi[];
}

export interface ProjectWorkflowSummary {
  workflow_id: string;
  team_title: string | null;
  statuses_count: number;
}

export interface IssueTypeApi {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  is_default: boolean;
  is_subtask: boolean;
  allowed_parent_type_ids: string[];
  /** id типов, для которых текущий тип может быть родителем. Непусто => тип потенциальный родитель. */
  child_type_ids: string[];
}

/**
 * Корневой тип каталога: не подзадача и не имеет валидных родителей. Для default это epic,
 * для других каталогов — program / strategy / phase / initiative / ticket. Корневые типы
 * исключаются из таблиц бэклога и не считаются «настоящим» родителем в иерархии доски.
 */
export function isRootIssueType(t: Pick<IssueTypeApi, 'is_subtask' | 'allowed_parent_type_ids'>): boolean {
  return !t.is_subtask && (t.allowed_parent_type_ids?.length ?? 0) === 0;
}

/** Корневые типы проекта по sort_order → name (ru). */
export function listSortedRootIssueTypes(issueTypes: IssueTypeApi[]): IssueTypeApi[] {
  return [...issueTypes]
    .filter(isRootIssueType)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'ru'));
}

/**
 * Основной корневой тип для дефолтного пула-родителя и иконки, когда строка родителя
 * без совпадения в каталоге: при наличии slug `epic` — он, иначе первый в порядке каталога.
 */
export function getPrimaryRootIssueType(issueTypes: IssueTypeApi[]): IssueTypeApi | undefined {
  const roots = listSortedRootIssueTypes(issueTypes);
  return roots.find((t) => t.slug === 'epic') ?? roots[0];
}

/** Строка `issue_type_ids` для listWorkIssues; `undefined`, если корневых типов нет. */
export function rootIssueTypeIdsQueryParam(issueTypes: IssueTypeApi[]): string | undefined {
  const ids = listSortedRootIssueTypes(issueTypes).map((t) => t.id);
  return ids.length ? ids.join(',') : undefined;
}

export function rootIssueTypeIdSet(issueTypes: Iterable<IssueTypeApi>): Set<string> {
  const set = new Set<string>();
  for (const t of issueTypes) {
    if (isRootIssueType(t)) set.add(t.id);
  }
  return set;
}

export interface LabelApi {
  id: string;
  name: string;
  normalized_name: string;
  color: string | null;
}

/**
 * Владение датами задачи роадмапом доски (work.issue.roadmap_ownership):
 * какая доска последней синкала start_at/due_at со своего роадмапа.
 * «Последний по set_at авторитетен» — остальные доски показывают шильдик
 * «Используется другим роадмапом» и не перезаписывают даты без подтверждения.
 */
export interface RoadmapOwnershipApi {
  /** documentName Yjs-комнаты доски-владельца (напр. `initiation-board:{pid}`). */
  board_key: string;
  /** Человекочитаемое имя доски для шильдика/ссылки. */
  board_name: string;
  /** ISO-момент взятия владения. */
  set_at: string;
  /** Даты изменены ВНЕ роадмапа (страница задачи) после взятия владения —
   *  карточка на роадмапе показывает шильдик «Дата изменена из раздела задач».
   *  Сбрасывается при следующем синке дат с самого роадмапа (перезапись
   *  ownership без этого поля). */
  external_date_change?: boolean;
}

/**
 * ПРОИСХОЖДЕНИЕ ЗАДАЧИ: доска, на которой задача родилась из черновика
 * (`work.issue_board_link`, changeset 0038). Родиться задача может ровно один
 * раз, поэтому поле одиночное — ни счётчика досок, ни списка.
 *
 * Название и ссылка приходят пустыми, если доску удалили либо зритель её не
 * видит: решение принимает сервер, интерфейс их не восстанавливает.
 */
export interface IssueBoardOriginApi {
  /** `'initiation'` — доска инициации проекта; иначе documentId КБ-доски. */
  board_key: string;
  board_title: string | null;
  board_href: string | null;
  /** Доску удалили: знак показываем серым — задача всё равно родилась там. */
  inactive: boolean;
}

export interface IssueListItemApi {
  id: string;
  key: string;
  seq: number;
  title: string;
  description: string | null;
  status_id: string;
  issue_type_id: string;
  parent_issue_id: string | null;
  priority: string;
  assignee_employee_id: string | null;
  reporter_employee_id: string | null;
  story_points: string | null;
  due_at: string | null;
  start_at: string | null;
  sprint_id: string | null;
  /** Команда задачи (team.title в проекте). NULL — задача в бэклоге проекта без команды. */
  team_title: string | null;
  rank: number | null;
  attachments_count: number;
  created_at: string;
  updated_at: string;
  /**
   * Счётчик версии строки задачи (`work.issue.version`). Им, а не `updated_at`,
   * сверяется оптимистичная блокировка: у отметки времени есть разрешающая
   * способность, и две правки внутри одной миллисекунды она не различает.
   * Отдаётся в `expected_version` при правке.
   */
  version: number;
  label_ids: string[];
  subtask_progress?: { done: number; total: number };
  /** Скрыта на доске «Бэклог задач» БЗ (карточка не рендерится). */
  hidden_on_board: boolean;
  /** Владение датами роадмапом доски; null = не привязана к роадмапу. Может отсутствовать на старом бэке. */
  roadmap_ownership?: RoadmapOwnershipApi | null;
  /**
   * Обложка карточки на доске Kanban: первое изображение из вложений
   * (own attachment > file БЗ). `null` если у задачи нет вложений-картинок.
   */
  cover_image_kind: 'attachment' | 'kb' | null;
  cover_image_id: string | null;
  /** Доска, на которой задача родилась. `null`/отсутствует — завели руками
   *  либо бэкенд ещё без этого поля. */
  board_origin?: IssueBoardOriginApi | null;
  /**
   * Все живые доски задачи (0040): отдаёт ТОЛЬКО деталь (getWorkIssue), в
   * списках поля нет. Объявлено здесь, потому что деталь в интерфейсе едет
   * тем же типом, что строка списка.
   */
  boards?: IssueBoardLinkApi[];
}

/**
 * Same-origin URL обложки задачи. Для own attachment'а нужен `issueId`,
 * для файла БЗ — нет. Возвращает `null`, если у задачи обложки нет.
 */
export function buildIssueCoverImageUrl(
  projectId: string,
  issueId: string,
  kind: IssueListItemApi['cover_image_kind'],
  coverId: string | null,
): string | null {
  if (!coverId) return null;
  if (kind === 'attachment') {
    return `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(coverId)}/stream`;
  }
  if (kind === 'kb') {
    return `/api/bff/projects/${encodeURIComponent(projectId)}/folder-files/${encodeURIComponent(coverId)}/stream`;
  }
  return null;
}

export interface IssueParentSummaryApi {
  id: string;
  key: string;
  title: string;
  issue_type_slug: string;
}

/**
 * ДОСКА, НА КОТОРОЙ ЛЕЖИТ КАРТОЧКА ЗАДАЧИ (0040, 16a4238b): любая живая связь —
 * происхождение, «ведут здесь» или просто «лежит» (present_on_board). Показ и
 * права — как у происхождения: имя и ссылка либо есть, либо сервер их не отдал.
 */
export interface IssueBoardLinkApi extends IssueBoardOriginApi {
  link_type: 'created_from_board' | 'placed_on_board' | 'present_on_board';
}

export interface IssueDetailApi extends IssueListItemApi {
  labels: LabelApi[];
  /** Родитель для хлебных крошек (история → эпик) */
  parent_issue?: IssueParentSummaryApi | null;
}

export interface IssuesListResponse {
  items: IssueListItemApi[];
  next_cursor: string | null;
  total?: number;
}

export interface SprintApi {
  id: string;
  project_id: string;
  name: string;
  goal: string | null;
  status: 'planned' | 'active' | 'completed';
  start_at: string | null;
  end_at: string | null;
  created_at: string;
  updated_at: string;
  /** Команда проекта (team.title), которой принадлежит спринт. NULL — legacy без команды. */
  team_title: string | null;
}

/**
 * 409 при попытке стартовать спринт, когда у команды уже есть active.
 * Возвращается из POST /work/sprints/:id/start без replace_active.
 */
export interface SprintStartConflict {
  code: 'ACTIVE_SPRINT_EXISTS';
  message: string;
  current_sprint_id: string;
  current_sprint_name: string;
  team_title: string;
}

export class SprintStartConflictError extends Error {
  readonly conflict: SprintStartConflict;
  constructor(conflict: SprintStartConflict) {
    super(conflict.message || 'Active sprint already exists');
    this.name = 'SprintStartConflictError';
    this.conflict = conflict;
  }
}

/** Убирает строковый «null»/пустоту — иначе BFF ищет несуществующий UUID родителя. */
function normalizeParentIssueIdForBody(
  id: string | null | undefined,
): string | null | undefined {
  if (id === undefined) return undefined;
  if (id === null) return null;
  const t = String(id).trim();
  if (t === '' || t.toLowerCase() === 'null') return null;
  return t;
}

function qs(params: Record<string, string | undefined>): string {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') u.set(k, v);
  });
  const s = u.toString();
  return s ? `?${s}` : '';
}

/**
 * Workflow конкретной команды (или legacy NULL workflow для бэклог-задач без команды).
 * Передавайте teamTitle = null/undefined для legacy общего workflow проекта.
 */
export async function getWorkWorkflow(
  projectId: string,
  teamTitle?: string | null,
): Promise<WorkflowResponse> {
  const query = qs({ team_title: teamTitle ?? undefined });
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/workflow${query}`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) {
    // 401 = сессия мертва → сигнал auth-watchdog (AuthTabSync уведёт на /auth).
    if (res.status === 401) notifyBffUnauthorized();
    await throwApiError(res);
  }
  return res.json();
}

/** Список всех workflow проекта по командам (для админ-страницы). */
export async function listProjectWorkflows(projectId: string): Promise<ProjectWorkflowSummary[]> {
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/workflows`, {
    headers: getBffRequestHeaders(),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/** Статус с привязкой к команде (для plain-lookup в shared-вьюхах). */
export interface ProjectStatusApi extends WorkflowStatusApi {
  team_title: string | null;
}

/**
 * Плоский список всех статусов проекта (по всем командам + legacy NULL).
 * Используется там, где список задач может содержать задачи из разных
 * команд (бэклог/таймлайн), и плашке статуса нужен lookup по status_id.
 */
export async function getAllProjectStatuses(projectId: string): Promise<ProjectStatusApi[]> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/all-statuses`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export interface CreateWorkflowStatusBody {
  name: string;
  category: 'todo' | 'in_progress' | 'in_review' | 'done';
  slug?: string;
  color?: string | null;
  sort_order?: number;
  after_status_id?: string | null;
}

function workflowQuery(teamTitle: string | null | undefined): string {
  return qs({ team_title: teamTitle ?? undefined });
}

export async function createWorkflowStatus(
  projectId: string,
  teamTitle: string | null | undefined,
  body: CreateWorkflowStatusBody,
): Promise<WorkflowStatusApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/workflow-statuses${workflowQuery(teamTitle)}`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function patchWorkflowStatus(
  projectId: string,
  teamTitle: string | null | undefined,
  statusId: string,
  body: { name?: string; color?: string | null; category?: string },
): Promise<WorkflowStatusApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/workflow-statuses/${encodeURIComponent(statusId)}${workflowQuery(teamTitle)}`,
    { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/**
 * 409 при удалении колонки с задачами без reassign_to_status_id.
 * UI ловит и показывает диалог с выбором target колонки.
 */
export interface ColumnHasIssuesConflict {
  code: 'COLUMN_HAS_ISSUES';
  issues_count: number;
  candidate_status_ids: string[];
  message: string;
}

export class ColumnHasIssuesError extends Error {
  readonly conflict: ColumnHasIssuesConflict;
  constructor(conflict: ColumnHasIssuesConflict) {
    super(conflict.message || 'Column has issues');
    this.name = 'ColumnHasIssuesError';
    this.conflict = conflict;
  }
}

export async function deleteWorkflowStatus(
  projectId: string,
  teamTitle: string | null | undefined,
  statusId: string,
  options?: { reassignToStatusId?: string | null },
): Promise<void> {
  const params: Record<string, string | undefined> = { team_title: teamTitle ?? undefined };
  if (options?.reassignToStatusId) params.reassign_to_status_id = options.reassignToStatusId;
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/workflow-statuses/${encodeURIComponent(statusId)}${qs(params)}`,
    { method: 'DELETE', headers: getBffRequestHeaders() },
  );
  if (res.ok) return;
  const text = await res.text();
  if (res.status === 409 && text.includes('COLUMN_HAS_ISSUES')) {
    // NestJS возвращает тело двумя возможными формами в зависимости от того,
    // как был сконструирован HttpException:
    //   1. payload в корне: { code, issues_count, candidate_status_ids, message }
    //   2. payload вложен:  { statusCode, message: { code, ... } }
    // Покрываем оба варианта.
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error(text);
    }
    const isCol = (v: unknown): v is ColumnHasIssuesConflict =>
      typeof v === 'object' &&
      v !== null &&
      (v as { code?: unknown }).code === 'COLUMN_HAS_ISSUES';
    let payload: ColumnHasIssuesConflict | null = null;
    if (isCol(parsed)) payload = parsed;
    else if (parsed && typeof parsed === 'object' && isCol((parsed as { message?: unknown }).message))
      payload = (parsed as { message: ColumnHasIssuesConflict }).message;
    if (payload) throw new ColumnHasIssuesError(payload);
  }
  throw new Error(text);
}

export async function duplicateWorkflowStatus(
  projectId: string,
  teamTitle: string | null | undefined,
  statusId: string,
): Promise<WorkflowStatusApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/workflow-statuses/${encodeURIComponent(statusId)}/duplicate${workflowQuery(teamTitle)}`,
    { method: 'POST', headers: jsonHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function reorderWorkflowStatuses(
  projectId: string,
  teamTitle: string | null | undefined,
  ids: string[],
): Promise<void> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/workflow-statuses/reorder${workflowQuery(teamTitle)}`,
    { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ ids }) },
  );
  if (!res.ok) await throwApiError(res);
}

export async function cloneTeamWorkflowFrom(
  projectId: string,
  teamTitle: string | null | undefined,
  sourceTeamTitle: string | null,
): Promise<WorkflowResponse> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/workflow/clone-from${workflowQuery(teamTitle)}`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ source_team_title: sourceTeamTitle }),
    },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export interface MoveTeamResult {
  moved: Array<{
    issue_id: string;
    prev_team_title: string | null;
    prev_status_id: string;
    new_team_title: string | null;
    new_status_id: string;
  }>;
  skipped: Array<{ issue_id: string; reason: string }>;
}

/** Перенос задач между командами (атомарный bulk). */
export async function moveIssuesAcrossTeams(
  projectId: string,
  body: {
    issue_ids: string[];
    target_team_title: string | null;
    status_mapping?: Record<string, string>;
    fallback_to_initial?: boolean;
    also_move_sprint_to?: string | null;
  },
): Promise<MoveTeamResult> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/move-team`,
    { method: 'POST', headers: idempotentJsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export interface TeamDeletionImpact {
  members_count: number;
  issues_count: number;
  sprints: { active: number; planned: number; finished: number };
  workflow_status_ids_in_use: string[];
}

export async function getTeamDeletionImpact(
  projectId: string,
  teamTitle: string,
): Promise<TeamDeletionImpact> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/teams/${encodeURIComponent(teamTitle)}/deletion-impact`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export interface DeleteTeamBody {
  reassign_to_team_title: string | null;
  status_mapping?: Record<string, string>;
  fallback_to_initial?: boolean;
  sprints_strategy: 'reassign' | 'delete_planned';
}

export async function deleteTeam(
  projectId: string,
  teamTitle: string,
  body: DeleteTeamBody,
): Promise<{
  moved_issues: number;
  moved_sprints: number;
  deleted_sprints: number;
  deleted_team_members: number;
}> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/teams/${encodeURIComponent(teamTitle)}`,
    { method: 'DELETE', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function listWorkIssueTypes(projectId: string): Promise<IssueTypeApi[]> {
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/issue-types`, {
    headers: getBffRequestHeaders(),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function createWorkIssueType(
  projectId: string,
  body: {
    name: string;
    slug?: string;
    color?: string | null;
    is_default?: boolean;
    is_subtask?: boolean;
    allowed_parent_type_ids?: string[];
  },
): Promise<IssueTypeApi> {
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/issue-types`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function listWorkLabels(projectId: string): Promise<LabelApi[]> {
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/labels`, {
    headers: getBffRequestHeaders(),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function createWorkLabel(
  projectId: string,
  body: { name: string; color?: string | null },
): Promise<LabelApi> {
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/labels`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function listWorkIssues(
  projectId: string,
  params: {
    cursor?: string;
    limit?: number;
    page?: number;
    q?: string;
    /** Точное совпадение ключа (для страницы задачи по URL) */
    issue_key?: string;
    status_id?: string;
    assignee_employee_id?: string;
    issue_type_id?: string;
    /** Несколько UUID через запятую (фильтр по нескольким типам) */
    issue_type_ids?: string;
    priority?: string;
    label_id?: string;
    parent_issue_id?: string | 'null';
    document_id?: string;
    mode?: 'top_level' | 'full';
    sprint_id?: string;
    backlog?: boolean;
    due_from?: string;
    due_to?: string;
    order_by?: string;
    order?: 'asc' | 'desc';
  },
): Promise<IssuesListResponse> {
  const url = `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues${qs({
    cursor: params.cursor,
    limit: params.limit != null ? String(params.limit) : undefined,
    page: params.page != null ? String(params.page) : undefined,
    q: params.q,
    issue_key: params.issue_key,
    status_id: params.status_id,
    assignee_employee_id: params.assignee_employee_id,
    issue_type_id: params.issue_type_id,
    issue_type_ids: params.issue_type_ids,
    priority: params.priority,
    label_id: params.label_id,
    parent_issue_id: params.parent_issue_id,
    document_id: params.document_id,
    mode: params.mode,
    sprint_id: params.sprint_id,
    backlog: params.backlog === true ? 'true' : undefined,
    due_from: params.due_from,
    due_to: params.due_to,
    order_by: params.order_by,
    order: params.order,
  })}`;
   
  console.log('[listWorkIssues URL]', url);
  const res = await fetch(url, { headers: getBffRequestHeaders() });
  if (!res.ok) {
    // 401 = сессия мертва → сигнал auth-watchdog (AuthTabSync уведёт на /auth).
    if (res.status === 401) notifyBffUnauthorized();
    await throwApiError(res);
  }
  const json = (await res.json()) as IssuesListResponse;
  rememberIssueVersions(json.items);
  return json;
}

/**
 * Multi-project список задач: `/api/bff/work/issues?projectIds=...`. См. BFF
 * `WorkMultiController.listMulti` и unified-scope §4.3 / §II.II аддендум.
 *
 * Каждый item обогащается серверным `project_id`; mutating-операции по-прежнему
 * per-project (через `listWorkIssues`/`patchWorkIssue`).
 */
/**
 * Категории workflow_status — фиксированный union (`work.workflow_status.category`
 * CHECK-констрейнт). Используется как ключ колонок multi-project Board view.
 */
export type WorkflowCategoryApi = 'todo' | 'in_progress' | 'in_review' | 'done';

export interface MultiProjectIssueListItemApi extends IssueListItemApi {
  project_id: string;
  /**
   * Категория статуса задачи на момент чтения. BFF обогащает
   * (см. WorkMultiController.listMulti — workflow_status батч на проект).
   * `null` — статус сослан на удалённый/осиротевший workflow_status;
   * Board view положит карточку в колонку «Без категории».
   */
  status_category: WorkflowCategoryApi | null;
}

export interface MultiProjectIssuesListResponse {
  items: MultiProjectIssueListItemApi[];
  total: number;
  accessible_project_count: number;
  /**
   * Legacy флаг hard-cap'а — включается, когда cursor отключён на BFF
   * (например, при custom `order_by`). В cursor-режиме всегда `false`;
   * используйте `next_cursor` для проверки «есть ли ещё страницы».
   */
  truncated: boolean;
  /**
   * Курсор следующей страницы. `null` — больше страниц нет. Формат
   * непрозрачный base64url; UI просто пробрасывает обратно в `params.cursor`.
   * См. BFF WorkMultiController.
   */
  next_cursor: string | null;
}

export async function listMultiProjectWorkIssues(
  projectIds: string[],
  params: {
    /** Размер страницы (cursor-режим). По умолчанию BFF: 50. Max: 200. */
    limit?: number;
    /** Cursor предыдущей страницы для infinite-scroll. */
    cursor?: string;
    q?: string;
    status_id?: string;
    assignee_employee_id?: string;
    issue_type_id?: string;
    issue_type_ids?: string;
    priority?: string;
    label_id?: string;
    mode?: 'top_level' | 'full';
    order_by?: string;
    order?: 'asc' | 'desc';
  },
): Promise<MultiProjectIssuesListResponse> {
  const url = `/api/bff/work/issues${qs({
    projectIds: projectIds.join(','),
    limit: params.limit != null ? String(params.limit) : undefined,
    cursor: params.cursor,
    q: params.q,
    status_id: params.status_id,
    assignee_employee_id: params.assignee_employee_id,
    issue_type_id: params.issue_type_id,
    issue_type_ids: params.issue_type_ids,
    priority: params.priority,
    label_id: params.label_id,
    mode: params.mode,
    order_by: params.order_by,
    order: params.order,
  })}`;
  const res = await fetch(url, { headers: getBffRequestHeaders() });
  if (!res.ok) await throwApiError(res);
  const json = (await res.json()) as MultiProjectIssuesListResponse;
  rememberIssueVersions(json.items);
  return json;
}

export async function listWorkSprints(
  projectId: string,
  params?: { status?: string; team_title?: string | null },
): Promise<SprintApi[]> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints${qs({
      status: params?.status,
      // null/undefined → не передаём; пустую строку шлём как "" для legacy-фильтра «без команды».
      team_title:
        params?.team_title === undefined
          ? undefined
          : params.team_title === null
          ? ''
          : params.team_title,
    })}`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/**
 * Возвращает active-спринт указанной команды (или null).
 * Без team_title — первый active без фильтра по команде (legacy).
 */
export async function getActiveWorkSprint(
  projectId: string,
  teamTitle?: string | null,
): Promise<SprintApi | null> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/active${qs({
      team_title:
        teamTitle === undefined
          ? undefined
          : teamTitle === null
          ? ''
          : teamTitle,
    })}`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  const json = (await res.json()) as { sprint: SprintApi | null };
  return json.sprint ?? null;
}

export async function createWorkSprint(
  projectId: string,
  body: {
    name: string;
    goal?: string | null;
    start_at?: string | null;
    end_at?: string | null;
    status?: 'planned' | 'active';
    team_title?: string | null;
  },
): Promise<SprintApi> {
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints`, {
    method: 'POST',
    headers: idempotentJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function patchWorkSprint(
  projectId: string,
  sprintId: string,
  body: Partial<{
    name: string;
    goal: string | null;
    start_at: string | null;
    end_at: string | null;
    team_title: string | null;
  }>,
): Promise<SprintApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}`,
    { method: 'PATCH', headers: idempotentJsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function deleteWorkSprint(projectId: string, sprintId: string): Promise<void> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}`,
    { method: 'DELETE', headers: idempotentHeaders() },
  );
  if (!res.ok) await throwApiError(res);
}

/**
 * Запуск спринта.
 *  • Если у команды нет active-спринта — обычный 200 со SprintApi.
 *  • Если есть конфликт и body.replace_active не передан — выбрасывается
 *    SprintStartConflictError с payload для UX-модалки.
 *  • С body.replace_active.move_unfinished старый active завершается атомарно.
 *
 * body.team_title обязателен, если у спринта team_title ещё не задан.
 */
export async function startWorkSprint(
  projectId: string,
  sprintId: string,
  body?: {
    team_title?: string | null;
    replace_active?: {
      move_unfinished: 'backlog' | 'new_sprint';
      new_sprint_id?: string | null;
    };
  },
): Promise<SprintApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}/start`,
    {
      method: 'POST',
      headers: idempotentJsonHeaders(),
      body: JSON.stringify(body ?? {}),
    },
  );
  if (!res.ok) {
    const rawText = await res.text();
    if (res.status === 409) {
      // NestException заворачивает payload-объект в { statusCode, message: payload, error }.
      try {
        const data = JSON.parse(rawText) as Record<string, unknown>;
        const inner =
          data && typeof data.message === 'object' && data.message !== null
            ? (data.message as Partial<SprintStartConflict> & { code?: string })
            : (data as Partial<SprintStartConflict> & { code?: string });
        if (
          inner?.code === 'ACTIVE_SPRINT_EXISTS' &&
          typeof inner.current_sprint_id === 'string' &&
          typeof inner.team_title === 'string'
        ) {
          throw new SprintStartConflictError({
            code: 'ACTIVE_SPRINT_EXISTS',
            message:
              typeof inner.message === 'string' ? inner.message : 'Active sprint already exists',
            current_sprint_id: inner.current_sprint_id,
            current_sprint_name: inner.current_sprint_name ?? '',
            team_title: inner.team_title,
          });
        }
      } catch (e) {
        if (e instanceof SprintStartConflictError) throw e;
        // JSON-parse error / shape mismatch — падаем на generic Error ниже
      }
    }
    throw new Error(rawText);
  }
  return res.json();
}

export async function completeWorkSprint(
  projectId: string,
  sprintId: string,
  body: { move_unfinished: 'backlog' | 'new_sprint'; new_sprint_id?: string | null },
): Promise<{ sprint: SprintApi }> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}/complete`,
    { method: 'POST', headers: idempotentJsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

// ===========================================================================
// Договорённости дня спринта (миграция BFF 114-sprint-day-note).
// ===========================================================================

/**
 * Срок жизни плашки баннера (миграция 117):
 *   • day    — до 22:00 сегодня (по серверному CURRENT_DATE MSK);
 *   • week   — до пт 22:00 ISO-недели, к которой относится `day` (пн);
 *   • sprint — пока спринт active.
 */
export type SprintDayNoteTimeframeApi = 'day' | 'week' | 'sprint';

/**
 * Подпись согласия с договором (миграция 115). Имя/avatar берутся из
 * employees.dynamic_fields. signed_at — момент подписи.
 */
export interface SprintDayNoteSignatureApi {
  employee_id: string;
  name: string;
  avatar_url: string | null;
  signed_at: string;
}

/**
 * Короткоживущая заметка-договор на конкретный день спринта.
 * day — строка YYYY-MM-DD (без времени), content — HTML от TipTap.
 * См. WorkService.mapDayNoteRow в BFF.
 */
export interface SprintDayNoteApi {
  id: string;
  sprint_id: string;
  day: string;
  timeframe: SprintDayNoteTimeframeApi;
  content: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  signatures: SprintDayNoteSignatureApi[];
  signatures_count: number;
  signed_by_me: boolean;
}

/**
 * Расширенный DTO для банера в левом сайдбаре: сегодняшняя заметка +
 * метаданные спринта/команды, чтобы фронт мог нарисовать переключатель
 * команд без дополнительных запросов.
 */
export interface ActiveDayNoteForBannerApi {
  sprint_id: string;
  project_id: string;
  project_key: string;
  team_title: string;
  sprint_name: string;
  note: SprintDayNoteApi;
}

/** Все заметки спринта (по возрастанию дня). Доступно участникам команды. */
export async function listSprintDayNotes(
  projectId: string,
  sprintId: string,
): Promise<SprintDayNoteApi[]> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}/day-notes`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  const data = (await res.json()) as { items: SprintDayNoteApi[] };
  return data.items;
}

/**
 * UPSERT заметки. `timeframe` определяет горизонт (день/неделя/спринт);
 * сервер сам определяет «опорный день» в Europe/Moscow и нормализует
 * `day` (для week — понедельник ISO-недели). Идемпотентен по partial-
 * unique-индексам миграции 117.
 */
export async function upsertDayNote(
  projectId: string,
  sprintId: string,
  timeframe: SprintDayNoteTimeframeApi,
  content: string,
  /**
   * id существующей заметки — для UPDATE по id (правка через попавер). Если
   * опущен — бэк делает классический UPSERT по partial-unique-индексу
   * timeframe и может создать новую запись.
   */
  noteId?: string,
): Promise<SprintDayNoteApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}/day-notes`,
    {
      method: 'POST',
      headers: idempotentJsonHeaders(),
      body: JSON.stringify({ content, timeframe, note_id: noteId }),
    },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/** Поставить подпись согласия (палец вверх) на сегодняшний договор. */
export async function putDayNoteSignatureMe(
  projectId: string,
  sprintId: string,
  noteId: string,
): Promise<SprintDayNoteApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}/day-notes/${encodeURIComponent(noteId)}/signatures/me`,
    { method: 'PUT', headers: idempotentJsonHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/** Снять собственную подпись с сегодняшнего договора. */
export async function deleteDayNoteSignatureMe(
  projectId: string,
  sprintId: string,
  noteId: string,
): Promise<SprintDayNoteApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/sprints/${encodeURIComponent(sprintId)}/day-notes/${encodeURIComponent(noteId)}/signatures/me`,
    { method: 'DELETE', headers: idempotentJsonHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/**
 * Сегодняшние заметки всех активных спринтов команд текущего пользователя.
 * Используется для банера в левом сайдбаре. Возвращает [] если ничего нет.
 */
export async function listMyActiveDayNotes(): Promise<ActiveDayNoteForBannerApi[]> {
  const res = await fetch('/api/bff/users/me/active-day-notes', {
    headers: getBffRequestHeaders(),
  });
  if (!res.ok) await throwApiError(res);
  const data = (await res.json()) as { items: ActiveDayNoteForBannerApi[] };
  return data.items;
}

/**
 * Изменить порядок задач (rank). Поддерживает cross-parent drag (передай
 * `parent_issue_id` в item — будет обновлён вместе с rank в одной транзакции).
 *
 * `currentSort` нужен только для «первого drag» в проекте: когда у проекта ещё
 * не выставлен флаг `rank_customized`, BFF переписывает ранг ВСЕХ задач по
 * этому критерию (см. `WorkService.reorderIssues`). На последующих вызовах
 * BFF этот параметр игнорирует.
 */
export async function reorderWorkIssues(
  projectId: string,
  items: { issue_id: string; rank: number; parent_issue_id?: string | null }[],
  opts: {
    currentSort?: { orderBy: string; order: 'asc' | 'desc' };
    /**
     * Полный новый порядок видимых задач после drag. На первом drag (когда
     * у проекта `rank_customized=false`) BFF использует ЭТО как авторитетный
     * источник нумерации рангов и игнорирует `items[].rank` (т.к. до первого
     * drag клиент не может посчитать осмысленный rank — данные NULL).
     */
    finalOrderIds?: string[];
  } = {},
): Promise<{ ok: boolean; rank_customized_now?: boolean }> {
  const body: Record<string, unknown> = { items };
  if (opts.currentSort) {
    body.current_sort_order_by = opts.currentSort.orderBy;
    body.current_sort_order = opts.currentSort.order;
  }
  if (opts.finalOrderIds && opts.finalOrderIds.length > 0) {
    body.final_order_ids = opts.finalOrderIds;
  }
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/reorder`, {
    method: 'POST',
    headers: idempotentJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function getWorkIssue(projectId: string, issueId: string): Promise<IssueDetailApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  const json = (await res.json()) as IssueDetailApi;
  rememberIssueVersion(json);
  return json;
}

export interface IssueActivityItemApi {
  event_id: string;
  event_type: string;
  issue_id: string;
  actor_employee_id: string | null;
  actor_name: string | null;
  actor_avatar_url: string | null;
  actor_via: string | null;
  aggregate_version: number | null;
  prev_payload: unknown;
  new_payload: unknown;
  occurred_at: string;
  correlation_id: string | null;
}

export interface IssueActivityResponseApi {
  items: IssueActivityItemApi[];
  next_cursor: string | null;
  /** UUID → читаемое имя (статусы, исполнители, типы, спринты, метки, parent-issue). */
  names: Record<string, string>;
}

export async function getIssueActivity(
  projectId: string,
  issueId: string,
  opts: { limit?: number; cursor?: string; eventTypes?: string[] } = {},
): Promise<IssueActivityResponseApi> {
  const url = new URL(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/activity`,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
  );
  if (opts.limit != null) url.searchParams.set('limit', String(opts.limit));
  if (opts.cursor) url.searchParams.set('cursor', opts.cursor);
  if (opts.eventTypes?.length) url.searchParams.set('event_types', opts.eventTypes.join(','));
  const path = url.pathname + url.search;
  const res = await fetch(path, { headers: getBffRequestHeaders() });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function createWorkIssue(
  projectId: string,
  body: {
    title: string;
    description?: string | null;
    issue_type_id: string;
    status_id?: string;
    parent_issue_id?: string | null;
    priority?: string;
    assignee_employee_id?: string | null;
    story_points?: number | null;
    due_at?: string | null;
    sprint_id?: string | null;
    start_at?: string | null;
    rank?: number | null;
    label_ids?: string[];
    /**
     * Команда задачи. Должна совпадать с workflow, из которого взят status_id.
     * Передаётся когда контекст создания — конкретная команда (канбан, «+ в
     * колонку»). Без неё бэкенд при отсутствии sprint_id/parent_issue_id
     * резолвит team_title в null и роняет 400 «status_id does not belong to
     * team workflow».
     */
    team_title?: string | null;
    /**
     * Откуда задача пришла: доска, на которой нажали «Создать задачу». Сервер
     * пишет связь в work.issue_board_link в одной транзакции с задачей.
     * `board_key` — 'initiation' либо document_id КБ-доски (та же кодировка,
     * что у ?board=); документ-доску выводит и сверяет с проектом сервер.
     */
    board_origin?: {
      board_key: string;
      board_title?: string | null;
      board_card_id?: string | null;
      link_type?: 'created_from_board' | 'placed_on_board';
    } | null;
  },
  options?: {
    /**
     * Стабильный Idempotency-Key для одного логического intent'а пользователя
     * (например, один черновик задачи). Передавай ОДИН и тот же ключ для всех
     * попыток сабмита одного и того же черновика — это закрывает дубли как от
     * случайных двойных кликов/двойных listener'ов, так и от сетевых retry.
     * Если не передан — генерируется новый UUID на каждый вызов.
     */
    idempotencyKey?: string;
  },
): Promise<IssueDetailApi> {
  const payload = {
    ...body,
    ...(body.parent_issue_id !== undefined
      ? { parent_issue_id: normalizeParentIssueIdForBody(body.parent_issue_id) ?? undefined }
      : {}),
  };
  const res = await fetch(`/api/bff/projects/${encodeURIComponent(projectId)}/work/issues`, {
    method: 'POST',
    headers: idempotentJsonHeaders(options?.idempotencyKey),
    body: JSON.stringify(payload),
  });
  if (!res.ok) await throwApiError(res);
  const json = (await res.json()) as IssueDetailApi;
  rememberIssueVersion(json);
  return json;
}

/**
 * ВЕРСИИ ЗАДАЧ, КОТОРЫЕ ЭТА ВКЛАДКА ВИДЕЛА (4891a256, решение владельца 24.09).
 *
 * Сервер сверяет правку по `expected_version` (счётчик строки, см.
 * IssueListItemApi.version) и отвечает 409 `stale_write`, если задачу успел
 * изменить кто-то другой. Клиент этой сверки не слал вообще: правки шли по
 * отметке времени с миллисекундным разрешением либо без сверки, и две правки
 * в одну миллисекунду перетирали друг друга.
 *
 * Мест правки задачи в интерфейсе — два десятка, и у половины под рукой нет
 * объекта задачи с версией (мутации по id из списка выделения, ячейки, доска).
 * Поэтому версия запоминается ЗДЕСЬ на каждом чтении задачи — списком, деталью,
 * ответом на правку — и подставляется в PATCH сама, если вызывающий не передал
 * свою. Реестр — на вкладку: это ровно то, что вкладка видела; другая вкладка
 * увидит своё и получит свой 409.
 */
const knownIssueVersions = new Map<string, number>();

/** Запомнить версию задачи из любого ответа, который её несёт. */
export function rememberIssueVersion(issue: { id?: string; version?: number } | null | undefined): void {
  if (!issue || typeof issue.id !== 'string' || !issue.id) return;
  if (typeof issue.version !== 'number' || !Number.isFinite(issue.version)) return;
  knownIssueVersions.set(issue.id, issue.version);
}

function rememberIssueVersions(items: ReadonlyArray<{ id?: string; version?: number }> | undefined): void {
  if (!Array.isArray(items)) return;
  for (const item of items) rememberIssueVersion(item);
}

/** Версия задачи, которую вкладка видела последней; undefined — не видела. */
export function knownIssueVersion(issueId: string): number | undefined {
  return knownIssueVersions.get(issueId);
}

/** Только для тестов: забыть всё, что вкладка видела. */
export function resetKnownIssueVersions(): void {
  knownIssueVersions.clear();
}

/**
 * ПОСЛЕ 409 stale_write — перечитать задачу везде, где она показана.
 *
 * Ключи запросов задач в приложении начинаются с `work-issue` (список, деталь
 * по ключу, быстрый просмотр, таймлайн, мульти-доска) либо содержат id задачи
 * (`board-all-work-issues` — список для доски). Инвалидируем все такие: 409 —
 * редкость, а перечитать лишнее дешевле, чем показать устаревшее.
 */
function invalidateIssueQueriesAfterStaleWrite(projectId: string, issueId: string): void {
  knownIssueVersions.delete(issueId);
  const client = getApiQueryClient();
  if (!client) return;
  void client.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      const head = typeof key[0] === 'string' ? key[0] : '';
      if (head.startsWith('work-issue') || head === 'board-all-work-issues') {
        return key.some((part) => part === projectId || part === issueId) || key.length === 1;
      }
      return key.some((part) => part === issueId);
    },
  });
}

/** 409 с кодом stale_write — сверка версии не сошлась. */
export function isStaleWriteError(err: unknown): boolean {
  const e = err as { status?: number; payload?: { code?: string } } | null;
  return Boolean(e && e.status === 409 && e.payload?.code === 'stale_write');
}

export async function patchWorkIssue(
  projectId: string,
  issueId: string,
  body: Partial<{
    /**
     * Сверка версии (см. knownIssueVersions): не передана — подставится та,
     * что вкладка видела последней. Явное `null` — не сверять (массовые
     * операции, где строка заведомо не читалась).
     */
    expected_version: number | null;
    title: string;
    description: string | null;
    status_id: string;
    issue_type_id: string;
    parent_issue_id: string | null;
    priority: string;
    assignee_employee_id: string | null;
    story_points: number | null;
    due_at: string | null;
    sprint_id: string | null;
    start_at: string | null;
    rank: number | null;
    label_ids: string[];
    hidden_on_board: boolean;
    convert_story_subtasks_to_tasks?: boolean;
    /** Владение датами роадмапом доски; null = отвязать от роадмапа. */
    roadmap_ownership: RoadmapOwnershipApi | null;
    /**
     * Доска, С КОТОРОЙ пришла правка. Полей задачи не меняет — освежает связь
     * задача↔доска, чтобы было видно, когда задачу последний раз вели отсюда.
     * Ключ доски сервер сверяет с проектом сам.
     */
    board_origin: {
      board_key: string;
      board_title?: string | null;
      board_card_id?: string | null;
    } | null;
  }>,
): Promise<IssueDetailApi> {
  const { expected_version: givenVersion, ...fields } = body;
  const expectedVersion =
    givenVersion === null ? undefined : (givenVersion ?? knownIssueVersion(issueId));
  const payload = {
    ...(fields.parent_issue_id !== undefined
      ? { ...fields, parent_issue_id: normalizeParentIssueIdForBody(fields.parent_issue_id) }
      : fields),
    ...(expectedVersion !== undefined ? { expected_version: expectedVersion } : {}),
  };
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}`,
    { method: 'PATCH', headers: idempotentJsonHeaders(), body: JSON.stringify(payload) },
  );
  /**
   * pre-beta-gate 3.12/14.6: 404 (задачу удалили, пока её редактировали) и
   * 409 (`stale_write` из optimistic-concurrency, см. `patch-issue.executor.ts`)
   * — оба реальных, ожидаемых кейса конкурентного редактирования, не просто
   * «что-то сломалось». `throwApiError` сохраняет status/payload, чтобы
   * вызывающий код мог показать `errorToUserMessage(e, …)` вместо сырого тела
   * ответа (было: `throw new Error(await res.text())` — пользователь видел
   * JSON `{"message":"Issue not found",...}` в тосте).
   */
  if (!res.ok) {
    try {
      await throwApiError(res);
    } catch (err) {
      // Устаревшая версия: перечитать задачу, ввод человека остаётся в поле —
      // он повторит правку поверх свежей (решение владельца 24.09, вариант А).
      if (isStaleWriteError(err)) invalidateIssueQueriesAfterStaleWrite(projectId, issueId);
      throw err;
    }
  }
  const json = (await res.json()) as IssueDetailApi;
  rememberIssueVersion(json);
  /**
   * Синхронизация кэша чата: BFF параллельно обновляет `chat.channel.name`
   * для канала issue-чата. Сбрасываем закэшированный `listChannels`, чтобы
   * сайдбар при следующем открытии подтянул актуальное название.
   */
  if (body.title !== undefined) {
    invalidateListChannelsCache();
  }
  return json;
}

export async function deleteWorkIssue(projectId: string, issueId: string): Promise<void> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}`,
    { method: 'DELETE', headers: idempotentHeaders() },
  );
  if (!res.ok) await throwApiError(res);
}

export async function duplicateWorkIssue(
  projectId: string,
  issueId: string,
  options?: { includeChildren?: boolean },
): Promise<IssueDetailApi> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/duplicate`,
    {
      method: 'POST',
      headers: idempotentJsonHeaders(),
      body: JSON.stringify({ include_children: options?.includeChildren === true }),
    },
  );
  if (!res.ok) await throwApiError(res);
  const json = (await res.json()) as IssueDetailApi;
  rememberIssueVersion(json);
  return json;
}

/** Название и подтип системной доски «Бэклог задач» — одни на проект. */
export const BACKLOG_BOARD_TITLE = 'Бэклог задач';
export const BACKLOG_BOARD_DOC_SUBTYPE = 'backlog_tasks';

export interface BacklogBoardDocumentResponse {
  projectId: string;
  documentId: string | null;
}

/**
 * [legacy] Возвращает `documentId` единой системной доски «Бэклог задач»
 * (`doc_subtype='backlog_tasks'`). Новые проекты эту доску не создают —
 * используйте `listBacklogPlanningSnapshots` / `createBacklogPlanningSnapshot`.
 */
export async function getBacklogBoardDocument(
  projectId: string,
): Promise<BacklogBoardDocumentResponse> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/backlog-board`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/** Снимок доски планирования из БЗ (папка «Бэклог досок»). */
export interface BacklogPlanningSnapshotItem {
  documentId: string;
  title: string;
  /** ISO-дата последнего изменения/создания (для сортировки). */
  updatedAt: string | null;
  folderId: string | null;
}

export interface BacklogPlanningSnapshotsResponse {
  projectId: string;
  items: BacklogPlanningSnapshotItem[];
}

export interface BacklogPlanningSnapshotCreatedResponse {
  projectId: string;
  documentId: string;
  folderId: string;
  title: string;
}

/** Список досок планирования проекта (новые сверху). */
export async function listBacklogPlanningSnapshots(
  projectId: string,
): Promise<BacklogPlanningSnapshotsResponse> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/backlog-planning/boards`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/**
 * Сформировать новую доску планирования: создаёт папку «Бэклог досок» (если
 * ещё нет), документ-доску с фиксированным списком видимых задач и возвращает
 * id для навигации. Дальше доска не синхронизируется с бэклогом.
 */
export async function createBacklogPlanningSnapshot(
  projectId: string,
): Promise<BacklogPlanningSnapshotCreatedResponse> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/backlog-planning/boards`,
    { method: 'POST', headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function listDocumentLinks(projectId: string, issueId: string) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/document-links`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function addDocumentLink(
  projectId: string,
  issueId: string,
  body: { document_id: string; link_role?: string; anchor?: string | null },
) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/document-links`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function deleteDocumentLink(projectId: string, issueId: string, linkId: string) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/document-links/${encodeURIComponent(linkId)}`,
    { method: 'DELETE', headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
}

export interface FolderFileLinkItem {
  id: string;
  folder_file_id: string;
  original_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  folder_id: string;
  created_at: string;
}

export async function listFolderFileLinks(
  projectId: string,
  issueId: string,
): Promise<FolderFileLinkItem[]> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/folder-file-links`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function addFolderFileLink(
  projectId: string,
  issueId: string,
  body: { folder_file_id: string },
): Promise<FolderFileLinkItem> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/folder-file-links`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function deleteFolderFileLink(
  projectId: string,
  issueId: string,
  linkId: string,
): Promise<void> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/folder-file-links/${encodeURIComponent(linkId)}`,
    { method: 'DELETE', headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
}

// ── Attachments: пользовательские файлы задачи (work.issue_attachment) ──
// Загружаются прямо в задачу, изолированы от БЗ (S3 префикс
// `work-attachments/`). UI — вкладка «Свои файлы» в блоке вложений
// детальной страницы задачи. См.
// `backend/.../src/bff/work-attachments/work-attachments.service.ts`.

export interface IssueAttachmentItem {
  id: string;
  issue_id: string;
  original_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_at: string | null;
  created_at: string;
  created_by: string | null;
}

export interface IssueAttachmentPresignResponse {
  attachment_id: string;
  upload_url: string;
  s3_key: string;
  expires_in_sec: number;
  max_bytes: number;
}

export async function listIssueAttachments(
  projectId: string,
  issueId: string,
): Promise<IssueAttachmentItem[]> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function presignIssueAttachment(
  projectId: string,
  issueId: string,
  body: { file_name: string; content_type?: string | null; size_bytes?: number | null },
): Promise<IssueAttachmentPresignResponse> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments/presign`,
    { method: 'POST', headers: idempotentJsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function commitIssueAttachment(
  projectId: string,
  issueId: string,
  attachmentId: string,
): Promise<IssueAttachmentItem> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(attachmentId)}/commit`,
    { method: 'POST', headers: idempotentJsonHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function deleteIssueAttachment(
  projectId: string,
  issueId: string,
  attachmentId: string,
): Promise<void> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: 'DELETE', headers: idempotentHeaders() },
  );
  if (!res.ok) await throwApiError(res);
}

/** Короткоживущий presigned GET для прямого скачивания файла. */
export async function getIssueAttachmentDownloadUrl(
  projectId: string,
  issueId: string,
  attachmentId: string,
): Promise<{ url: string; file_name: string; mime_type: string | null }> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(attachmentId)}/download-url`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  const data = (await res.json()) as { url?: string; file_name?: string; mime_type?: string | null };
  if (!data.url) throw new Error('No url in response');
  return {
    url: data.url,
    file_name: data.file_name || 'file',
    mime_type: data.mime_type ?? null,
  };
}

/** Same-origin путь к BFF-стриму тела вложения (для превью pdf/docx/xlsx/pptx). */
export function issueAttachmentStreamUrl(
  projectId: string,
  issueId: string,
  attachmentId: string,
): string {
  return `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(attachmentId)}/stream`;
}

export async function fetchIssueAttachmentStreamBuffer(
  projectId: string,
  issueId: string,
  attachmentId: string,
): Promise<ArrayBuffer> {
  const res = await fetch(issueAttachmentStreamUrl(projectId, issueId, attachmentId), {
    headers: { ...getBffRequestHeaders(), Accept: '*/*' },
  });
  if (!res.ok) await throwApiError(res);
  return res.arrayBuffer();
}

/** Same-origin путь к BFF-стриму PDF-превью PPTX-вложения. */
export function issueAttachmentPptxPreviewPdfStreamUrl(
  projectId: string,
  issueId: string,
  attachmentId: string,
): string {
  return `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(attachmentId)}/preview-pdf-stream`;
}

/**
 * PDF-превью PPTX-вложения через BFF-поток. `null` при 503/404/400 — тот же
 * контракт, что у `fetchKbFolderFilePptxPreviewPdfBuffer` для файлов БЗ.
 */
export async function fetchIssueAttachmentPptxPreviewPdfBuffer(
  projectId: string,
  issueId: string,
  attachmentId: string,
): Promise<ArrayBuffer | null> {
  const res = await fetch(
    issueAttachmentPptxPreviewPdfStreamUrl(projectId, issueId, attachmentId),
    { headers: { ...getBffRequestHeaders(), Accept: 'application/pdf' } },
  );
  if (res.status === 503 || res.status === 404 || res.status === 400) {
    return null;
  }
  if (!res.ok) await throwApiError(res);
  return res.arrayBuffer();
}

export const ISSUE_ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;

/**
 * Сквозной helper загрузки: presign → прямой PUT в MinIO → commit.
 * Возвращает финальный объект `IssueAttachmentItem` (с size_bytes и uploaded_at).
 * Бросает с понятным сообщением, если файл больше {@link ISSUE_ATTACHMENT_MAX_BYTES}.
 */
export async function uploadIssueAttachment(
  projectId: string,
  issueId: string,
  file: File,
): Promise<IssueAttachmentItem> {
  if (file.size > ISSUE_ATTACHMENT_MAX_BYTES) {
    throw new Error(`Файл больше ${Math.round(ISSUE_ATTACHMENT_MAX_BYTES / 1024 / 1024)} МБ`);
  }
  const presigned = await presignIssueAttachment(projectId, issueId, {
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
  });
  const putRes = await fetch(presigned.upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!putRes.ok) {
    throw new Error(`PUT в хранилище не удался: ${putRes.status} ${putRes.statusText}`);
  }
  return commitIssueAttachment(projectId, issueId, presigned.attachment_id);
}

export async function listVcsRefs(projectId: string, issueId: string) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/vcs-refs`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function addVcsRef(
  projectId: string,
  issueId: string,
  body: {
    provider: string;
    repo_ref?: string;
    commit_sha: string;
    message?: string | null;
    web_url?: string | null;
  },
) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/vcs-refs`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function deleteVcsRef(projectId: string, issueId: string, refId: string) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/vcs-refs/${encodeURIComponent(refId)}`,
    { method: 'DELETE', headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
}

export interface VcsConnectionApi {
  id: string;
  project_id: string;
  provider: string;
  repo_full_name: string;
  /** Заполнено, если webhook создан через GitHub API */
  webhook_id?: string | null;
  created_at: string;
  updated_at: string;
  revoked_at: string | null;
}

export interface CreateVcsConnectionResponse extends VcsConnectionApi {
  inbound_token: string;
  auto_registered?: boolean;
}

export async function listVcsConnections(projectId: string): Promise<VcsConnectionApi[]> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/vcs-connections`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function createVcsConnection(
  projectId: string,
  body: {
    provider: 'github' | 'gitlab';
    repo_full_name: string;
    webhook_secret?: string;
    github_personal_access_token?: string;
  },
): Promise<CreateVcsConnectionResponse> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/vcs-connections`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function deleteVcsConnection(projectId: string, connectionId: string): Promise<{ ok: boolean }> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/vcs-connections/${encodeURIComponent(connectionId)}`,
    { method: 'DELETE', headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function listModuleLinks(projectId: string, issueId: string) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/module-links`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function addModuleLink(
  projectId: string,
  issueId: string,
  body: {
    installation_id: string;
    module_code: string;
    resource_type: string;
    resource_id?: string | null;
    payload?: Record<string, unknown> | null;
  },
) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/module-links`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function deleteModuleLink(projectId: string, issueId: string, linkId: string) {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/${encodeURIComponent(issueId)}/module-links/${encodeURIComponent(linkId)}`,
    { method: 'DELETE', headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
}

// ── Раскладка полотна «Все команды» (общая, на проект) ──────────────

export interface TeamBoardsLayoutItemDto {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface TeamBoardsLayoutDto {
  project_id: string;
  layout: TeamBoardsLayoutItemDto[];
  mounted_tiles: string[];
  updated_at: string;
  updated_by_employee_id: string | null;
}

/**
 * Возвращает сохранённую раскладку «Все команды» проекта или null,
 * если ничего ещё не сохранено. Право: work:team_boards:view_all.
 */
export async function getTeamBoardsLayout(
  projectId: string,
): Promise<TeamBoardsLayoutDto | null> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/team-boards-layout`,
    { headers: getBffRequestHeaders() },
  );
  if (!res.ok) await throwApiError(res);
  const data = (await res.json()) as TeamBoardsLayoutDto | null;
  return data ?? null;
}

/**
 * Сохраняет (UPSERT) раскладку. Перезатирает любые предыдущие значения
 * (last-write-wins). Право: work:team_boards:view_all.
 */
export async function saveTeamBoardsLayout(
  projectId: string,
  body: { layout: TeamBoardsLayoutItemDto[]; mounted_tiles: string[] },
): Promise<TeamBoardsLayoutDto> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/team-boards-layout`,
    { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

// ---------- Bulk import задач (CSV/XLSX) ----------

export interface BulkImportRow {
  local_id: number;
  parent_local_id?: number | null;
  key?: string | null;
  parent_key?: string | null;
  title: string;
  description?: string | null;
  type_slug?: string | null;
  type_name?: string | null;
  status_name?: string | null;
  priority?: string | null;
  assignee_email?: string | null;
  assignee_name?: string | null;
  reporter_email?: string | null;
  reporter_name?: string | null;
  story_points?: string | number | null;
  start_at?: string | null;
  due_at?: string | null;
  labels?: string[] | null;
  team_title?: string | null;
  rank?: number | null;
}

export interface BulkImportPayload {
  mode: 'append' | 'replace';
  type_mapping?: Record<string, string>;
  status_mapping?: Record<string, string>;
  label_strategy?: 'create_missing' | 'drop_missing';
  issues: BulkImportRow[];
}

export interface BulkImportSuccess {
  ok: true;
  created_count: number;
  deleted_count: number;
}

export interface BulkImportRowError {
  local_id: number | null;
  field?: string;
  reason: string;
}

export type BulkImportApiError =
  | { code: 'VALIDATION_FAILED'; status: 422; errors: BulkImportRowError[] }
  | { code: 'IDEMPOTENCY_CONFLICT'; status: 409 }
  | { code: 'IMPORT_IN_PROGRESS'; status: 409 }
  | { code: 'IMPORT_FAILED'; status: 500 | 502 | 503 | 504; message: string };

export class BulkImportError extends Error {
  readonly detail: BulkImportApiError;
  constructor(detail: BulkImportApiError) {
    super(
      detail.code === 'IMPORT_FAILED'
        ? detail.message
        : `${detail.code} (HTTP ${detail.status})`,
    );
    this.name = 'BulkImportError';
    this.detail = detail;
  }
}

/**
 * Идемпотентный bulk-import задач. `idempotencyKey` — UUID v4 со стороны клиента;
 * передавайте один и тот же ключ при ретраях того же файла (это безопасно), и
 * новый — при новом импорте.
 *
 * Любая 4xx-ошибка (валидация/конфликт/lock) приходит как `BulkImportError`;
 * автоматический retry должен делаться ТОЛЬКО для сетевых/5xx, и только с
 * исходным `idempotencyKey` — иначе можно случайно создать дубликаты.
 */
export async function bulkImportWorkIssues(
  projectId: string,
  payload: BulkImportPayload,
  idempotencyKey: string,
): Promise<BulkImportSuccess> {
  const res = await fetch(
    `/api/bff/projects/${encodeURIComponent(projectId)}/work/issues/bulk-import`,
    {
      method: 'POST',
      headers: { ...jsonHeaders(), 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(payload),
    },
  );
  if (res.ok) return res.json();

  const text = await res.text();
  let parsed: Record<string, unknown> | null = null;
  try { parsed = text ? (JSON.parse(text) as Record<string, unknown>) : null; } catch { parsed = null; }
  const code = String(parsed?.code ?? '');

  if (res.status === 422 && code === 'VALIDATION_FAILED') {
    throw new BulkImportError({
      code: 'VALIDATION_FAILED',
      status: 422,
      errors: Array.isArray(parsed?.errors) ? (parsed!.errors as BulkImportRowError[]) : [],
    });
  }
  if (res.status === 409 && code === 'IDEMPOTENCY_CONFLICT') {
    throw new BulkImportError({ code: 'IDEMPOTENCY_CONFLICT', status: 409 });
  }
  if (res.status === 409) {
    throw new BulkImportError({ code: 'IMPORT_IN_PROGRESS', status: 409 });
  }
  throw new BulkImportError({
    code: 'IMPORT_FAILED',
    status: res.status as 500 | 502 | 503 | 504,
    message: text || res.statusText,
  });
}
